import { useMemo, useCallback } from 'react'
import {
  ORDER_DISCOUNT_CONDITION_MIN_SPEND,
  ORDER_DISCOUNT_CONDITION_MIN_QUANTITY,
  ORDER_DISCOUNT_TYPE_FIXED,
  ORDER_DISCOUNT_TYPE_PERCENTAGE,
} from '_utils/constant'

export const useOrderDiscount = ({
  orderDiscountEnabled = false,
  orderDiscount = null,
  itemsTotal = 0,
  adminCost = 0,
  newOrderProducts = [],
  quantities = {},
  newOrderProductsIndividual = [],
  edittingOrder = false,
}) => {
  const calculateTotalQuantity = useCallback(() => {
    let qty = 0
    newOrderProducts?.forEach((cur) => {
      qty += cur.quantity || 0
      if (cur.splitCasePurchase && cur.splitCasePurchase?.enabled) {
        const splitQty = quantities[`${cur.id}-${cur.index}-split`] ?? 0
        qty += splitQty
      }
    })
    if (edittingOrder) {
      newOrderProductsIndividual?.forEach((cur) => {
        qty += cur.quantity || 0
      })
      Object.keys(quantities || {}).forEach((key) => {
        if (!key.includes('-split')) return
        const splitQty = quantities[key] || 0
        const parts = key.split('-')
        if (parts.length < 3) return
        const productId = parts.slice(0, parts.length - 2).join('-')
        const index = parseInt(parts[parts.length - 2], 10)
        const existingIndividual = newOrderProductsIndividual?.find(
          (ind) => ind.id === productId && ind.index === index
        )
        const existingQty = existingIndividual ? existingIndividual.quantity : 0
        const delta = splitQty - existingQty
        if (delta > 0) {
          qty += delta
        }
      })
    }
    return qty
  }, [newOrderProducts, quantities, edittingOrder, newOrderProductsIndividual])

  const discountAmount = useMemo(() => {
    if (!orderDiscountEnabled || !orderDiscount) return 0
    if (itemsTotal <= 0) return 0

    const { condition, threshold, type, amount } = orderDiscount
    const numThreshold = Number(threshold) || 0
    const numAmount = Number(amount) || 0
    if (numAmount <= 0) return 0

    let isEligible = false
    if (condition === ORDER_DISCOUNT_CONDITION_MIN_SPEND) {
      isEligible = numThreshold <= 0 || itemsTotal >= numThreshold
    } else if (condition === ORDER_DISCOUNT_CONDITION_MIN_QUANTITY) {
      const itemsQty = calculateTotalQuantity()
      isEligible = numThreshold <= 0 || itemsQty >= numThreshold
    } else {
      isEligible = numThreshold <= 0 || itemsTotal >= numThreshold
    }

    if (!isEligible) return 0

    if (type === ORDER_DISCOUNT_TYPE_FIXED) {
      return Math.min(itemsTotal, numAmount)
    }

    if (type === ORDER_DISCOUNT_TYPE_PERCENTAGE) {
      return (itemsTotal * numAmount) / 100
    }

    return 0
  }, [orderDiscountEnabled, orderDiscount, itemsTotal, calculateTotalQuantity])

  const adminFee = useMemo(() => (adminCost > 0 ? Number(adminCost) : 0), [adminCost])
  const originalTotal = useMemo(
    () => (itemsTotal > 0 ? itemsTotal + adminFee : 0),
    [itemsTotal, adminFee]
  )
  const finalTotal = useMemo(
    () => Math.max(0, originalTotal - discountAmount),
    [originalTotal, discountAmount]
  )

  return {
    discountAmount,
    adminFee,
    originalTotal,
    finalTotal,
    totalQuantity: calculateTotalQuantity(),
  }
}

export default useOrderDiscount
